import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { ApiError, api } from '@/api/client'

const nullableString = z.string().nullable().optional()

const inviteUserSchema = z
  .object({
    id: z.number().optional(),
    display_name: nullableString,
    first_name: nullableString,
    last_name: nullableString,
    profile_image: nullableString,
  })
  .passthrough()
  .nullable()
  .optional()

const pouleInvitePreviewSchema = z
  .object({
    poule: z
      .object({
        id: z.number(),
        name: z.string(),
        description: nullableString,
        visibility: nullableString,
        requires_approval: z.boolean().optional(),
        owner_name: nullableString,
        admin_names: z.array(z.string()).optional().default([]),
        thumbnail_url: nullableString,
        hero_url: nullableString,
        member_count: z.number().optional(),
      })
      .passthrough()
      .nullable(),
    inviter: inviteUserSchema,
    invite_valid: z.boolean(),
    reason: z.string().optional().default('invalid'),
    membership_status_for_current_user: z.string().optional().default('none'),
  })
  .passthrough()

const pouleInviteJoinSchema = z
  .object({
    status: z.string(),
    membership_status: z.string().optional(),
    poule_id: z.number().nullable().optional(),
    reason: z.string().optional(),
  })
  .passthrough()

const managerInvitePreviewSchema = z
  .object({
    league: z
      .object({
        id: z.number(),
        name: z.string(),
        season_id: z.number().nullable().optional(),
      })
      .passthrough()
      .nullable(),
    subleague: z
      .object({
        id: z.number(),
        fantasy_league_id: z.number(),
        name: z.string(),
        slug: z.string().optional(),
        visibility: z.string().optional(),
      })
      .passthrough()
      .nullable(),
    inviter: inviteUserSchema,
    invite_valid: z.boolean(),
    reason: z.string().optional().default('invalid'),
    main_league_membership_status_for_current_user: z.string().optional().default('none'),
    subleague_membership_status_for_current_user: z.string().optional().default('none'),
    next_action: z.string().optional().default('invalid_invite'),
  })
  .passthrough()

const managerLeagueJoinSchema = z
  .object({
    membership: z
      .object({
        status: z.string().nullable().optional(),
        role: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  })
  .passthrough()

const managerInviteJoinSchema = z
  .object({
    status: z.string(),
    fantasy_league_id: z.number().nullable().optional(),
    subleague_id: z.number().nullable().optional(),
    reason: z.string().optional(),
  })
  .passthrough()

export type PouleInvitePreview = z.infer<typeof pouleInvitePreviewSchema>
export type ManagerInvitePreview = z.infer<typeof managerInvitePreviewSchema>

export type InvitePreview =
  | { kind: 'poule'; data: PouleInvitePreview }
  | { kind: 'manager'; data: ManagerInvitePreview }

export type InviteKind = 'auto' | 'poule' | 'manager'

function encodedToken(token: string) {
  return encodeURIComponent(token.trim())
}

async function fetchPouleInvite(token: string) {
  try {
    const response = await api.get<unknown>(`/poule-invites/${encodedToken(token)}`)
    const parsed = pouleInvitePreviewSchema.safeParse(response)
    if (!parsed.success) throw new Error('De poule-invite kon niet worden gelezen.')
    return parsed.data
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null
    throw error
  }
}

async function fetchManagerInvite(token: string) {
  try {
    const response = await api.get<unknown>(
      `/fantasy-subleague-invites/${encodedToken(token)}`,
    )
    const parsed = managerInvitePreviewSchema.safeParse(response)
    if (!parsed.success) throw new Error('De Manager-invite kon niet worden gelezen.')
    return parsed.data
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null
    throw error
  }
}

async function fetchInvitePreview(token: string, kind: InviteKind): Promise<InvitePreview | null> {
  if (kind === 'manager') {
    const data = await fetchManagerInvite(token)
    return data ? { kind: 'manager', data } : null
  }

  const poule = await fetchPouleInvite(token)
  if (poule) return { kind: 'poule', data: poule }
  if (kind === 'poule') return null

  const manager = await fetchManagerInvite(token)
  return manager ? { kind: 'manager', data: manager } : null
}

export function useInvitePreview(token: string | undefined, kind: InviteKind = 'auto') {
  return useQuery({
    queryKey: ['invite-preview', kind, token],
    queryFn: () => fetchInvitePreview(token!, kind),
    enabled: Boolean(token),
    staleTime: 30_000,
  })
}

async function joinPouleInvite(token: string) {
  const response = await api.post<unknown>(`/poule-invites/${encodedToken(token)}/join`, {})
  const parsed = pouleInviteJoinSchema.safeParse(response)
  if (!parsed.success) throw new Error('Deelnemen aan de poule is niet gelukt.')
  return parsed.data
}

async function joinManagerLeague(leagueId: number) {
  const response = await api.post<unknown>(`/fantasy-leagues/${leagueId}/join`, {})
  const parsed = managerLeagueJoinSchema.safeParse(response)
  if (!parsed.success || !parsed.data.membership) {
    throw new Error('Deelnemen aan de Manager-league is niet gelukt.')
  }
  return parsed.data
}

async function joinManagerSubleague(token: string) {
  const response = await api.post<unknown>(
    `/fantasy-subleague-invites/${encodedToken(token)}/join`,
    {},
  )
  const parsed = managerInviteJoinSchema.safeParse(response)
  if (!parsed.success) throw new Error('Deelnemen aan de Manager-poule is niet gelukt.')
  return parsed.data
}

export function useJoinPouleInvite(token: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => joinPouleInvite(token!),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['poule-info', result.poule_id] }),
        queryClient.invalidateQueries({ queryKey: ['joined-poules'] }),
        queryClient.invalidateQueries({ queryKey: ['public-poules'] }),
        queryClient.invalidateQueries({ queryKey: ['public-poule-search'] }),
      ])
    },
  })
}

export function useJoinManagerLeagueFromInvite(
  token: string | undefined,
  leagueId: number | undefined,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!leagueId) throw new Error('Manager-league ontbreekt.')
      return joinManagerLeague(leagueId)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['manager-home'] }),
        queryClient.invalidateQueries({ queryKey: ['public-manager-leagues'] }),
        queryClient.invalidateQueries({ queryKey: ['manager-league-detail', leagueId] }),
        queryClient.invalidateQueries({ queryKey: ['invite-preview', 'auto', token] }),
        queryClient.invalidateQueries({ queryKey: ['invite-preview', 'manager', token] }),
      ])
    },
  })
}

export function useJoinManagerSubleagueInvite(token: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => joinManagerSubleague(token!),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['manager-home'] }),
        queryClient.invalidateQueries({ queryKey: ['public-manager-leagues'] }),
        queryClient.invalidateQueries({ queryKey: ['manager-league-detail', result.fantasy_league_id] }),
      ])
    },
  })
}
